/**
 * Step 3 — Fases contratadas (contract_mode por phase).
 *
 * AI-1.f.bug — Las phases ya NO son del HSL hardcoded. Vienen de
 * `state.phaseDefs`, que se computa al seleccionar el lifecycle en
 * Step 1 (`buildPhaseDefsFromDefinition` extrae las phases del
 * `definition.phases` del current_version). Si el lifecycle declara
 * `contract_mode_overridable=false` para una phase, el selector
 * queda deshabilitado (como un soft-lock).
 *
 * Esto se persiste en `metadata.phases` del proyecto. Cuando el backend
 * materializa el lifecycle (vía `lifecycle_template_id` en POST), las
 * phases reales se crean desde el snapshot — `metadata.phases` queda
 * solo como auditoría de qué eligió el user en el wizard.
 */

import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'

import {
  type ContractModeChoice,
  type PhaseDef,
  type WizardState,
} from './types'

interface Step3Props {
  state: WizardState
  onSetMode: (code: string, mode: ContractModeChoice) => void
}

export function Step3Phases({ state, onSetMode }: Step3Props) {
  const { t } = useTranslation('projects')

  return (
    <div className="p-6">
      <h3 className="font-display text-lg font-semibold mb-1 text-ink">
        {t('wizard.step3.title')}
      </h3>
      <p className="text-sm text-ink-muted font-body mb-1">
        {t('wizard.step3.subtitle')}
      </p>
      {state.lifecycleTemplate ? (
        <p className="text-[11px] text-ink-subtle mb-4 font-mono">
          {state.lifecycleTemplate.templateKey} · v
          {state.lifecycleTemplate.version}
        </p>
      ) : null}

      {/* Leyenda */}
      <div className="flex items-center gap-4 mb-5 px-1 text-xs text-ink-muted flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-brand" />
          <strong>{t('contract_mode.internal')}</strong>{' '}
          · {t('wizard.step3.legend.internal')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-line-strong" />
          <strong>{t('contract_mode.skipped')}</strong>{' '}
          · {t('wizard.step3.legend.skipped')}
        </span>
      </div>

      {state.phaseDefs.length === 0 ? (
        <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-sm text-ink-muted">
          {t('wizard.step3.no_phases')}
        </div>
      ) : (
        <div className="border border-line rounded-md divide-y divide-line bg-surface">
          {state.phaseDefs.map((phase) => (
            <PhaseRow
              key={phase.code}
              phase={phase}
              mode={state.phases[phase.code] ?? phase.defaultMode}
              onChange={(mode) => onSetMode(phase.code, mode)}
            />
          ))}
        </div>
      )}

      <div className="bg-brand-soft/30 border border-brand/25 rounded-md p-3 mt-4 flex items-start gap-3 text-xs">
        <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
        <p
          className="font-body text-ink-muted leading-relaxed [&_strong]:text-ink"
          // i18n contiene <strong> — render permitido por seguridad (sin user input).
          dangerouslySetInnerHTML={{ __html: t('wizard.step3.info') }}
        />
      </div>
    </div>
  )
}

function PhaseRow({
  phase,
  mode,
  onChange,
}: {
  phase: PhaseDef
  mode: ContractModeChoice
  onChange: (mode: ContractModeChoice) => void
}) {
  const { t } = useTranslation('projects')
  const isSkipped = mode === 'skipped'
  const isLocked = !phase.available || !phase.overridable
  const rowBg = isSkipped && !phase.available ? 'opacity-60' : ''

  // Subhint: cantidad de subphases + expected artifacts (si aplica).
  const hint = (() => {
    const parts: string[] = []
    if (phase.subphaseCount > 0) {
      parts.push(
        t('wizard.step3.phase_subphases_count', {
          count: phase.subphaseCount,
        }),
      )
    }
    if (phase.expectedArtifactCount > 0) {
      parts.push(
        t('wizard.step3.phase_artifacts_count', {
          count: phase.expectedArtifactCount,
        }),
      )
    }
    if (!phase.overridable) {
      parts.push(t('wizard.step3.phase_locked'))
    }
    return parts.join(' · ')
  })()

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${rowBg}`}>
      <span className="w-7 h-7 rounded-full bg-brand-soft text-brand grid place-items-center text-xs font-semibold shrink-0">
        {phase.order}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">{phase.name}</p>
        {hint ? <p className="text-[10px] text-ink-muted">{hint}</p> : null}
      </div>
      <select
        value={mode}
        onChange={(e) => onChange(e.target.value as ContractModeChoice)}
        disabled={isLocked}
        className={
          !isLocked
            ? 'bg-canvas border border-line rounded-md px-2.5 py-1.5 text-xs w-44'
            : 'bg-canvas border border-line rounded-md px-2.5 py-1.5 text-xs w-44 cursor-not-allowed text-ink-subtle'
        }
      >
        <option value="internal">{t('wizard.step3.select.internal')}</option>
        <option value="skipped">{t('wizard.step3.select.skipped')}</option>
      </select>
    </div>
  )
}
